import { useEffect, useMemo } from "react"
import { isEqual } from "lodash-es"
import { makeStyles } from "tss-react/mui"
import { useField } from "formik"
import { Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { CreateOrderDialogType, ExtraServiceDetails, ProductWithQuantity } from "../../../createOrderTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { StandardButton } from "../../../../buttons/StandardButton"
import { calculateClampedAmount, isServiceAdded } from "./helpers"
import { ExtraServicesAccordion } from "./ExtraServicesAccordion"

const useStyles = makeStyles()((theme) => ({
    icon: {
        color: theme.palette.tailwind.slate[400],
    },
    wrap: {
        pointerEvents: "auto",
    },
}))

type ExtraServicesContentProps = Readonly<{
    services: ExtraServiceDetails[]
    dialogType: CreateOrderDialogType
}>

export function ExtraServicesContent({ services, dialogType }: ExtraServicesContentProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    const isRefundOrder = dialogType === CreateOrderDialogType.CREDIT_NOTE

    const [{ value: deliveries }, , deliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )
    const [{ value: seatCountValue }] = useField(DynamicConfigKey.NUMBER_OF_SEATS)

    const seatCount = seatCountValue || 0

    const servicesMap = useMemo(() => {
        const map = new Map<number, ExtraServiceDetails>()
        for (const service of services) {
            map.set(service.id, service)
        }
        return map
    }, [services])

    useEffect(() => {
        if (!seatCount || !Array.isArray(deliveries?.deliveryTimes)) return

        const newValue = {
            ...deliveries,
            deliveryTimes: deliveries.deliveryTimes.map((delivery) => ({
                ...delivery,
                orderLines: delivery.orderLines.map((orderLine) => {
                    const service = servicesMap.get(orderLine.id)
                    if (!service || !orderLine.isExtraService) return orderLine

                    const updatedPrice = { ...service.pricePerSeat, amount: calculateClampedAmount(service, seatCount) }

                    return {
                        ...orderLine,
                        price: updatedPrice,
                        adjustedPrice: updatedPrice,
                    }
                }),
            })),
        }

        if (!isEqual(deliveries, newValue)) {
            deliveriesHelpers.setValue(newValue)
        }
    }, [seatCount, servicesMap, deliveries.deliveryTimes, deliveriesHelpers])

    const handleAddService = (deliveryTime: string, service: ExtraServiceDetails) => {
        const totalPrice = { ...service.pricePerSeat, amount: calculateClampedAmount(service, seatCount) }

        const newOrderLine: ProductWithQuantity = {
            id: service.id,
            name: service.name,
            price: totalPrice,
            isActive: service.isActive,
            quantity: 1,
            isExtraService: true,
        }

        const updatedDeliveries = deliveries.deliveryTimes.map((delivery) => {
            if (delivery.time !== deliveryTime) return delivery
            if (isServiceAdded(delivery, service.id)) return delivery

            return {
                ...delivery,
                orderLines: [...delivery.orderLines, newOrderLine],
            }
        })

        deliveriesHelpers.setValue({
            ...deliveries,
            deliveryTimes: updatedDeliveries,
        })
    }

    const handleRemoveService = (deliveryTime: string, serviceId: number) => {
        const updatedDeliveries = deliveries.deliveryTimes.map((delivery) => {
            if (delivery.time !== deliveryTime) return delivery

            return {
                ...delivery,
                orderLines: delivery.orderLines.filter((p) => !(p.id === serviceId && p.isExtraService)),
            }
        })

        deliveriesHelpers.setValue({
            ...deliveries,
            deliveryTimes: updatedDeliveries,
        })
    }

    return (
        <Stack gap={2} className={classes.wrap}>
            <LocalizedStrict id="create-order-extra-services">
                <Typography variant="subtitle1">Extra services</Typography>
            </LocalizedStrict>

            {deliveries.deliveryTimes.map((delivery, index) => {
                const time = delivery.time!
                return (
                    <LocalizedStrict
                        id="create-order-extra-services-delivery"
                        attrs={{ title: true }}
                        vars={{ time }}
                        key={`delivery-${time}-${index}`}
                    >
                        <ExtraServicesAccordion title={`Delivery at ${time}`}>
                            {services.map((service) => {
                                const { id, name, description, pricePerSeat, minimumPrice, maximumPrice } = service
                                const total = { ...pricePerSeat, amount: calculateClampedAmount(service, seatCount) }
                                const isAlreadyAdded = isServiceAdded(delivery, id)

                                return (
                                    <Stack gap=".75rem" key={id}>
                                        <Stack gap=".25rem">
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                gap="1rem"
                                            >
                                                <Stack flexDirection="row" gap=".5rem" alignItems="center">
                                                    <Typography fontWeight="bold">{name}</Typography>
                                                    <Tooltip title={description}>
                                                        <FontAwesomeIcon icon={faCircleInfo} className={classes.icon} />
                                                    </Tooltip>
                                                </Stack>
                                                <Typography variant="subtitle1">
                                                    {moneyFactory.format(toDinero(total))}
                                                </Typography>
                                            </Stack>

                                            <LocalizedStrict
                                                id="create-order-extra-services-min-max"
                                                vars={{
                                                    min: moneyFactory.format(toDinero(minimumPrice)),
                                                    max: moneyFactory.format(toDinero(maximumPrice)),
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    Min. {moneyFactory.format(toDinero(minimumPrice))} - Max.
                                                    {moneyFactory.format(toDinero(maximumPrice))}
                                                </Typography>
                                            </LocalizedStrict>
                                        </Stack>

                                        <Stack
                                            flexDirection="row"
                                            gap="1rem"
                                            justifyContent="flex-end"
                                            alignItems="center"
                                        >
                                            <Typography variant="subtitle2">
                                                {seatCount} seats x {moneyFactory.format(toDinero(pricePerSeat))}
                                            </Typography>
                                            {isAlreadyAdded ? (
                                                <LocalizedStrict id="create-order-extra-services-remove-button">
                                                    <StandardButton
                                                        size="small"
                                                        fullWidth={false}
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handleRemoveService(time, id)}
                                                        disabled={isRefundOrder}
                                                    >
                                                        Remove
                                                    </StandardButton>
                                                </LocalizedStrict>
                                            ) : (
                                                <LocalizedStrict id="create-order-extra-services-add-button">
                                                    <StandardButton
                                                        size="small"
                                                        fullWidth={false}
                                                        onClick={() => handleAddService(time, service)}
                                                        disabled={isRefundOrder}
                                                    >
                                                        Add
                                                    </StandardButton>
                                                </LocalizedStrict>
                                            )}
                                        </Stack>
                                    </Stack>
                                )
                            })}
                        </ExtraServicesAccordion>
                    </LocalizedStrict>
                )
            })}
        </Stack>
    )
}
